import {
  ActionIcon,
  DropdownMenu,
  TableCell,
  TableRow,
} from '@medeloop/medeloop-components-library';
import { Cohort, CohortStepStatus, CohortStepType } from 'src/types/cohorts';
import { JobProgressIndicator } from '../common/job-progress-indicator';
import { ReactNode } from 'react';

export interface RowAction<T> {
  name: string | ReactNode;
  icon?: ReactNode;
  onClick: (item: T) => void;
}

interface CohortRowProps {
  cohort: Cohort;
  onClick?: () => void;
  rowActions: RowAction<Cohort>[] | null;
  isHighlighted?: boolean;
}

export const CohortRow = ({
  cohort,
  onClick,
  rowActions,
  isHighlighted = false,
}: CohortRowProps) => {
  // Check if the cohort is in progress and in the Results step
  const isInProgress =
    cohort.currentStep === CohortStepType.Results &&
    cohort.steps?.[CohortStepType.Results]?.status === CohortStepStatus.In_Progress;

  if (isInProgress) {
    return (
      <tr>
        <td colSpan={6} className="p-0">
          <JobProgressIndicator
            createdAt={new Date(cohort.createdAt!)}
            estimatedDuration={18}
            progressHead={`Preparing "${cohort.name}" cohort`}
          />
        </td>
      </tr>
    );
  }

  return (
    <TableRow
      className={`hover:bg-cool-gray-800 cursor-pointer ${
        isHighlighted ? 'bg-purple-900 hover:bg-purple-800/50' : ''
      } ${cohort.archivedAt ? 'bg-background-dark-subdued3' : ''}`}
      onClick={onClick}
    >
      <td className="p-4">
        <div className="flex items-center space-x-4">
          {/* Name and description */}
          <div className="flex flex-col">
            <span className="text-base font-semibold text-white">{cohort.name}</span>
            <span className="text-sm text-content-on-dark-subdued">{cohort.description}</span>
          </div>
        </div>
      </td>
      <TableCell tooltipText={cohort.dataSourceName || ''}>
        {cohort.dataSourceName || '-'}
      </TableCell>
      <TableCell tooltipText={cohort.createdByEmail || ''}>
        {cohort.createdByEmail || '-'}
      </TableCell>
      <TableCell tooltipText={`${cohort.dataWiki?.metadata?.record_count || 0} records`}>
        {cohort.dataWiki?.metadata?.record_count || '-'}
      </TableCell>
      <TableCell tooltipText={cohort.createdAt ? new Date(cohort.createdAt).toISOString() : ''}>
        {cohort.createdAt ? new Date(cohort.createdAt).toISOString().split('T')[0] : '-'}
      </TableCell>
      {rowActions ? (
        <td className="p-4">
          <DropdownMenu
            component={<ActionIcon name="edit" />}
            items={rowActions?.map((action) => ({
              ...action,
              onClick: () => action.onClick(cohort),
            })) ?? []}
          />
        </td>
        ) :
        null
      }
    </TableRow>
  );
};
